import { useCallback, ReactNode } from 'react'
import { Plus } from 'lucide-react'
import { shallowEqual } from 'react-redux'
import { Select } from '@components/formFields/select'
import { useAppSelector } from '@redux/hooks'
import { Field, TemplateField } from '@templates/types'

interface FieldSet extends TemplateField {
  fields: Field[]
}

interface Props {
  fieldsetId: number
  name: string
  onSelectCallback: (field: Field) => void
  excludeFieldId: number[]
  noOptions?: ReactNode
}

interface Option {
  id: number
  name: string
}

export const SelectGroupedByFieldSet = ({
  fieldsetId,
  name,
  onSelectCallback,
  excludeFieldId,
  noOptions = null,
}: Props) => {
  const fieldset: FieldSet = useAppSelector(
    state => state.Template.fieldsetsById[fieldsetId],
    shallowEqual,
  )
  const fields = fieldset?.fields
  const options = fields
    ?.map(f => ({ id: f.id, name: f.name }) as Option)
    .filter(f => !excludeFieldId.includes(f.id))

  const onSelectCallbackHandler = useCallback(
    (selectedItem: number | string) => {
      const selectedField = fields.find(f => f.id === selectedItem)
      if (!selectedField) return

      onSelectCallback(selectedField)
    },
    [fields],
  )

  if (!options || (Array.isArray(options) && options?.length === 0)) return noOptions

  return (
    <Select
      name={name}
      popoverId={`select-field-from-fieldset-${fieldsetId}`}
      data={options}
      selectedItems={[]}
      multi={false}
      onSelectCallback={onSelectCallbackHandler}
      icon={<Plus size={16} />}
    >
      <Select.VirtualList>
        {(index, column) => (
          <Select.Option key={column.id} id={column.id} index={index}>
            {column.name}
          </Select.Option>
        )}
      </Select.VirtualList>
    </Select>
  )
}
